import React from "react";
import { motion } from "framer-motion";
import { Stethoscope, Scale, Briefcase, Globe2, FileText, ArrowUpRight } from "lucide-react";

const MEDICAL_IMG = "/medical-interpreter.png";
const LEGAL_IMG = "/legal-interpreter.png";
const BUSINESS_IMG = "/business-interpreter.png";
const TRANSLATION_IMG = "/translation-service.png";
const IMMIGRATION_IMG =
  "https://images.pexels.com/photos/4427611/pexels-photo-4427611.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const services = [
  {
    id: "medical",
    icon: Stethoscope,
    title: "Medical Interpreting",
    desc: "Patient intake, specialist consults, mental health, OB-GYN, and follow-up care — interpreted with empathy and clinical accuracy.",
    bullets: ["Hospitals & clinics", "Telehealth visits", "Patient education"],
    image: MEDICAL_IMG,
  },
  {
    id: "legal",
    icon: Scale,
    title: "Legal Interpreting",
    desc: "Trials, arbitrations, hearings, depositions, attorney-client meetings, and document review — every nuance preserved, every confidence kept.",
    bullets: ["Trials, arbitrations & hearings", "Depositions & mediations", "Attorney-client meetings"],
    image: LEGAL_IMG,
  },
  {
    id: "immigration",
    icon: Globe2,
    title: "Immigration Interpreting",
    desc: "USCIS interviews, asylum prep, consular appointments, and immigration attorney consults — handled with patience, accuracy, and complete confidentiality.",
    bullets: ["USCIS & asylum interviews", "Attorney consultations", "Document review support"],
    image: IMMIGRATION_IMG,
  },
  {
    id: "business",
    icon: Briefcase,
    title: "Business Interpreting",
    desc: "Conferences, negotiations, HR onboarding, and community outreach — bridging Spanish-speaking teams and partners with poise.",
    bullets: ["Meetings & negotiations", "HR & onboarding", "Community outreach"],
    image: BUSINESS_IMG,
  },
  {
    id: "translation",
    icon: FileText,
    title: "Document Translation",
    desc: "Accurate Spanish ↔ English translation of personal, legal, and business documents — formatted to match the original and delivered ready to use.",
    bullets: [
      "Birth, marriage & death certificates",
      "IDs, passports & official letters",
      "Books, brochures & business documents",
    ],
    image: TRANSLATION_IMG,
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="relative">
      <div className="container-edge py-20 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-14 md:mb-20">
          <div className="lg:col-span-7">
            <div className="eyebrow" data-testid="services-eyebrow">What I Offer</div>
            <h2
              className="mt-5 font-display text-[hsl(var(--ink))] text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]"
              data-testid="services-heading"
            >
              Interpreting that meets the moment —
              <span className="italic text-[hsl(var(--accent))]"> clinical, legal, human.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pl-8">
            <p className="text-[hsl(var(--text-muted))] text-base leading-relaxed">
              Every conversation deserves to be understood the first time. Our
              vetted, qualified interpreters work on-site and remotely across
              medical, legal, immigration, and business settings — partnering
              with providers, attorneys, and organizations who value accuracy
              as much as compassion.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[hsl(var(--line))] border border-[hsl(var(--line))]">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.article
                key={s.id}
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                custom={i}
                data-testid={`service-card-${s.id}`}
                className="group bg-[hsl(var(--surface))] p-8 md:p-10 flex flex-col gap-6 transition-colors duration-300 hover:bg-[hsl(var(--bg))]"
              >
                {s.image ? (
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={s.image}
                      alt={s.title}
                      className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                ) : (
                  <div className="relative aspect-[4/3] overflow-hidden bg-[hsl(var(--ink))] text-white flex items-end p-6">
                    <Icon size={56} strokeWidth={1} className="text-[hsl(var(--accent))]" />
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <Icon size={18} className="text-[hsl(var(--accent))]" />
                  <h3
                    data-testid={`service-title-${s.id}`}
                    className="font-display text-2xl text-[hsl(var(--ink))]"
                  >
                    {s.title}
                  </h3>
                </div>

                <p className="text-[hsl(var(--text-muted))] leading-relaxed">{s.desc}</p>

                <ul className="mt-auto space-y-2 pt-2 border-t border-[hsl(var(--line))]">
                  {s.bullets.map((b) => (
                    <li
                      key={b}
                      className="text-sm text-[hsl(var(--ink))] flex items-center gap-3"
                    >
                      <span className="w-1.5 h-1.5 bg-[hsl(var(--accent))]" />
                      {b}
                    </li>
                  ))}
                </ul>

                <a
                  href="#contact"
                  data-testid={`service-cta-${s.id}`}
                  className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.18em] font-semibold text-[hsl(var(--ink))] hover:text-[hsl(var(--accent))] transition-colors"
                >
                  Request this service <ArrowUpRight size={14} />
                </a>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
